import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// store
import {
  dbDataStore,
  messageAndErrorStore,
  projectStore,
  userStore,
} from "../../store/Store";
// assets
import Close from "../../assets/close.svg";

// components
import DropDown from "../../formComponents/DropDown";
import FormButton from "../../formComponents/FormButton";
// funcs
import { parseDateInCentral } from "../../funcs/helperFuncs";
import { useEscape } from "../../hooks/useEscape";
//
export default function AddProjectRateSheet({
  setAddProjectRsTrigger,
  pRsId,
  setEditId,
}) {
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const currentProject = projectStore((data) => data.currentProject);
  const projectCarriers = projectStore((data) => data.projectCarriers);
  const setProjectRateSheets = projectStore(
    (data) => data.setProjectRateSheets,
  );
  const setProjectLoads = projectStore((data) => data.setProjectLoads);

  const rateSheetData = dbDataStore((data) => data.rateSheetData);
  const tieredRateSheets = dbDataStore((data) => data.tieredRateSheets);
  const fuelSurchargeData = dbDataStore((data) => data.fuelSurchargeData);
  const companyData = dbDataStore((data) => data.companyData);
  const toggleNewMessage = messageAndErrorStore(
    (data) => data.toggleNewMessage,
  );

  const [vendorOptions, setVendorOptions] = useState([]);
  const [rateSheetOptions, setRateSheetOptions] = useState([]);
  const [tierRateSheetOptions, setTierRateSheetOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [pending, setPending] = useState(false);
  const [rsType, setRsType] = useState();
  const [isTier, setIsTier] = useState(false);
  const [isAllVendors, setIsAllVendors] = useState(false);
  const [newRs, setNewRs] = useState({
    id: 0,
    project_id: currentProject.project.id,
    account_company_id: 0,
    is_customer: false,
    pc_carrier_id: "",
    rate_sheet_id: 0,
    start_date: null,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    is_fsc: false,
    rate_sheet_tier_id: 0,
  });
  const navigate = useNavigate();

  const rsTypeOptions = [
    { value: 0, label: "Freight" },
    { value: 1, label: "FSC" },
  ];
  var id = 0;
  if (pRsId) {
    id = pRsId;
  }

  // get project rate sheet data if updating
  useEffect(() => {
    if (pRsId) {
      const headers = new Headers();
      const controller = new AbortController();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/project-rate-sheet/${pRsId}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              navigate("/login");
            } else if (res.status === 403) {
              toggleNewMessage("Forbidden");
            } else {
              toggleNewMessage("Could not get rate sheet data");
            }
            return Promise.resolve();
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            data.start_date = data.start_date.slice(0, 10);
            setNewRs(data);
            if (data.is_fsc) {
              setRsType(1);
            } else {
              setRsType(0);
            }
          }
        })
        .catch((err) => {
          toggleNewMessage(err);
          return;
        });
      return () => controller.abort();
    }
  }, [pRsId, JwtToken, navigate, toggleNewMessage]);
  // get tiers
  useEffect(() => {
    if (newRs.rate_sheet_id !== 0 && isTier) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/rate-sheet-tiers/${newRs.rate_sheet_id}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 403) {
              setAuthStatus(false);
              navigate("/login");
            } else {
              toggleNewMessage("Could not get rate sheet tiers");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            let arr = [];
            data.forEach((t) => {
              arr.push({ value: t.id, label: `Tier ${t.tier_number}` });
            });
            setTierOptions(arr);
          }
        })
        .catch((err) => {
          toggleNewMessage("Something went wrong");
          return;
        });
    }
  }, [
    newRs.rate_sheet_id,
    isTier,
    JwtToken,
    navigate,
    toggleNewMessage,
    setTierOptions,
    setAuthStatus,
  ]);
  // get company options
  useEffect(() => {
    const companies = [
      {
        value: 0,
        label: "All Vendors",
      },
      {
        value: currentProject.project.customer_id,
        label: currentProject.project.customer_name,
      },
      {
        value: currentProject.project.vendor_id,
        label: currentProject.project.vendor_name,
      },
    ];
    if (projectCarriers) {
      projectCarriers.forEach((c) => {
        if (!c.is_pass_through) {
          companies.push({ value: c.pc_carrier_id, label: c.carrier_name });
        }
      });
    }
    setVendorOptions(companies);
  }, [companyData, projectCarriers, currentProject]);

  // get rate sheet options
  useEffect(() => {
    const rateSheets = [];
    if (rsType === 0) {
      rateSheetData.forEach((rs) => {
        rateSheets.push({ value: rs.id, label: rs.rs_name });
      });
    } else if (rsType === 1) {
      fuelSurchargeData.forEach((rs) => {
        rateSheets.push({ value: rs.id, label: rs.fsc_name });
      });
    }
    setRateSheetOptions(rateSheets);
  }, [rsType, rateSheetData, fuelSurchargeData]);

  // tier rate sheet options
  useEffect(() => {
    if (tieredRateSheets) {
      let rateSheets = [];
      tieredRateSheets.forEach((r) => {
        rateSheets.push({ value: r.id, label: r.rs_name });
      });
      setTierRateSheetOptions(rateSheets);
    }
  }, [tieredRateSheets]);

  const handleClose = () => {
    setAddProjectRsTrigger(false);
    setEditId("");
  };

  useEscape(setAddProjectRsTrigger, [setEditId], [""]);

  const handleSelect = (o, e) => {
    switch (e.name) {
      case "rs_type":
        setRsType(o.value);
        setNewRs({
          ...newRs,
          is_fsc: o.value === 0 ? false : true,
        });
        break;
      case "company":
        if (o.label === "All Vendors") {
          setIsAllVendors(true);
        } else if (typeof o.value === "string") {
          setNewRs({
            ...newRs,
            pc_carrier_id: o.value,
            account_company_id: 0,
          });
        } else {
          setNewRs({
            ...newRs,
            account_company_id: o.value,
            pc_carrier_id: "",
          });
        }
        break;
      case "rate_sheet_id":
        setNewRs({
          ...newRs,
          rate_sheet_id: o.value,
        });
        break;
      case "tier_rate_sheet_id":
        setNewRs({
          ...newRs,
          rate_sheet_id: o.value,
        });
        setIsTier(true);
        break;
      case "rate_sheet_tier_id":
        setNewRs({
          ...newRs,
          rate_sheet_tier_id: o.value,
        });
        break;
      default:
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    var reqBody = { ...newRs };
    reqBody.start_date = parseDateInCentral(reqBody.start_date, false);
    if (reqBody.account_company_id === currentProject.project.customer_id) {
      reqBody.is_customer = true;
    }
    var method = "POST";
    if (id > 0) {
      method = "PATCH";
    }
    const headers = new Headers();
    const controller = new AbortController();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);

    const requestOptions = {
      method,
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/project-freight-rate-sheet/${id}?is_all_vendors=${isAllVendors}`,
      requestOptions,
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            navigate("/login");
            setPending(false);
          } else if (res.status === 403) {
            toggleNewMessage("Forbidden");
            return Promise.resolve();
          } else {
            toggleNewMessage(`Something went wrong: ${res.statusText}`);
            return Promise.resolve();
          }
          setPending(false);
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          return;
        } else {
          setProjectRateSheets(data.project_rate_sheets);
          setProjectLoads(data.project_loads);
          setPending(false);
          toggleNewMessage(null, "Project Rate Sheet Added");
          setAddProjectRsTrigger(false);
          setEditId("")
        }
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage(err);
      })
      .finally(() => {
        setPending(false);
        setEditId("");
      });
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Add Project Rate Sheet</h3>
        <form onSubmit={handleSubmit}>
          <DropDown
            title="Type"
            name="rs_type"
            options={rsTypeOptions}
            value={rsTypeOptions.filter((x) => x.value === rsType)}
            onChange={handleSelect}
            placeholder="Type"
            required={true}
          />
          <DropDown
            title="Company"
            name="company"
            options={vendorOptions}
            onChange={handleSelect}
            value={
              isAllVendors
                ? { value: 0, label: "All Vendors" }
                : newRs.pc_carrier_id !== ""
                  ? vendorOptions.filter(
                      (x) => x.value === newRs.pc_carrier_id,
                    )[0]
                  : newRs.account_company_id !== 0 &&
                    vendorOptions.filter(
                      (x) => x.value === newRs.account_company_id,
                    )[0]
            }
            placeholder="Comany"
            required={true}
          />
          {rateSheetOptions.length > 0 &&
            !(
              newRs.account_company_id === currentProject.project.customer_id &&
              currentProject.project.rate_sheet_tier_id !== 0
            ) && (
              <DropDown
                title="Rate Sheet"
                name="rate_sheet_id"
                options={rateSheetOptions}
                value={
                  rateSheetOptions.filter(
                    (x) => x.value === newRs.rate_sheet_id,
                  )[0]
                }
                onChange={handleSelect}
                placeholder="Rate Sheet"
                required={!isTier ? true : false}
              />
            )}
          {newRs.account_company_id === currentProject.project.customer_id &&
            rsType === 0 &&
            tierRateSheetOptions.length > 0 &&
            currentProject.project.rate_sheet_tier_id > 0 && (
              <DropDown
                title="Tier Rate Sheet"
                name="tier_rate_sheet_id"
                options={tierRateSheetOptions}
                value={
                  tierRateSheetOptions.filter(
                    (x) => x.value === newRs.rate_sheet_id,
                  )[0]
                }
                onChange={handleSelect}
                placeholder="Tier Rate Sheet"
                required={newRs.rate_sheet_id === 0 ? true : false}
              />
            )}
          {tierOptions.length > 0 && (
            <DropDown
              title="Default Rate Sheet Tier"
              name="rate_sheet_tier_id"
              options={tierOptions}
              value={
                tierOptions.filter(
                  (x) => x.value === newRs.rate_sheet_tier_id,
                )[0]
              }
              onChange={handleSelect}
              placeholder="Tier"
              required={isTier ? true : false}
            />
          )}
          {newRs.rate_sheet_id > 0 && (
            <div className="mb-1 row field">
              <label className="col-sm-4 col-form-label fw-bold">
                Start Date
              </label>
              <div className="col-sm-4">
                <input
                  name="start_date"
                  type="date"
                  required
                  onChange={(e) =>
                    setNewRs({ ...newRs, start_date: e.target.value })
                  }
                  value={newRs.start_date || ""}
                />
              </div>
            </div>
          )}
          <FormButton pending={pending} name="Submit" />
        </form>
      </div>
    </div>
  );
}
